package org.teddapp.project.ui.portfolio

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.launch
import com.tedd.db.Competence
import com.tedd.db.Competence_criterion
import com.tedd.db.Competence_family
import com.tedd.db.Portfolio_piece
import org.teddapp.project.db.repo.PieceCompetenceRef
import org.teddapp.project.portfolio.CompetenceLevel
import org.teddapp.project.portfolio.PieceImage
import org.teddapp.project.portfolio.PieceLink
import org.teddapp.project.portfolio.PieceStatus
import org.teddapp.project.portfolio.PortfolioCodecs
import org.teddapp.project.ui.LocalUseFullScreenForm
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddDateField
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/**
 * Cadre commun des éditeurs : dialogue centré sur grand écran, plein écran sur téléphone.
 * Le contenu défile ; les actions restent en bas.
 */
@Composable
private fun EditorChrome(
    title: String,
    onClose: () -> Unit,
    actions: @Composable RowScope.() -> Unit,
    content: @Composable ColumnScope.() -> Unit,
) {
    val fullScreen = LocalUseFullScreenForm.current
    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        val shape = RoundedCornerShape(if (fullScreen) 0.dp else TeddTokens.RXl)
        Column(
            modifier = Modifier
                .then(if (fullScreen) Modifier.fillMaxSize() else Modifier.width(560.dp).heightIn(max = 760.dp))
                .clip(shape)
                .background(TeddTokens.Bg, shape)
                .padding(horizontal = 22.dp, vertical = 20.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(bottom = 14.dp)) {
                Text(title, style = TeddType.Title2, color = TeddTokens.Ink, modifier = Modifier.weight(1f))
                TeddIconButton(onClick = onClose, size = 30.dp) { TeddIcon(name = "chevron-left", size = 14.dp, color = TeddTokens.InkMuted) }
            }
            Column(
                modifier = Modifier.weight(1f, fill = false).verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(14.dp),
                content = content,
            )
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) { actions() }
        }
    }
}

/** Champ multi-lignes au même dessin que TeddField. */
@Composable
private fun PfMultiline(label: String, value: String, onChange: (String) -> Unit, placeholder: String, minHeight: Dp = 96.dp, mono: Boolean = false) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Column {
        PfEyebrow(label, modifier = Modifier.padding(bottom = 6.dp, start = 2.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = minHeight)
                .clip(shape)
                .background(TeddTokens.Surface, shape)
                .border(1.dp, TeddTokens.Line, shape)
                .padding(horizontal = 12.dp, vertical = 10.dp),
        ) {
            if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkMuted, fontSize = 13.sp, lineHeight = 19.sp)
            BasicTextField(
                value = value,
                onValueChange = onChange,
                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp, lineHeight = 19.sp, fontFamily = if (mono) TeddType.Mono.fontFamily else null),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun PaletteRow(label: String, options: List<String>, selected: String, onSelect: (String) -> Unit, icon: Boolean) {
    Column {
        PfEyebrow(label, modifier = Modifier.padding(bottom = 6.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            options.forEach { opt ->
                val on = opt == selected
                if (icon) {
                    Box(
                        modifier = Modifier
                            .size(30.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(if (on) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onSelect(opt) },
                        contentAlignment = Alignment.Center,
                    ) { TeddIcon(name = opt, size = 14.dp, color = if (on) TeddTokens.OnAction else TeddTokens.InkMuted) }
                } else {
                    val c = parseHex(opt, TeddTokens.LimeStrong)
                    Box(
                        modifier = Modifier
                            .size(26.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(c, RoundedCornerShape(TeddTokens.RSm))
                            .then(if (on) Modifier.border(2.dp, TeddTokens.Ink, RoundedCornerShape(TeddTokens.RSm)) else Modifier)
                            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onSelect(opt) },
                    )
                }
            }
        }
    }
}

// ── Famille ────────────────────────────────────────────────────────────────

@Composable
internal fun FamilyEditorDialog(existing: Competence_family?, model: PortfolioModel) {
    var name by remember(existing) { mutableStateOf(existing?.name ?: "") }
    var description by remember(existing) { mutableStateOf(existing?.description ?: "") }
    var color by remember(existing) { mutableStateOf(existing?.color ?: FAMILY_PALETTE.first()) }
    var icon by remember(existing) { mutableStateOf(existing?.icon ?: FAMILY_ICONS.first()) }
    EditorChrome(
        title = if (existing == null) "Nouvelle famille" else "Modifier la famille",
        onClose = { model.editor = null },
        actions = {
            if (existing != null) {
                TeddButton(text = "Supprimer", onClick = { model.editor = null; model.confirmDelete = DeleteTarget.Family(existing) }, kind = TeddButtonKind.Danger, size = TeddButtonSize.Md)
            }
            Spacer(modifier = Modifier.weight(1f))
            TeddButton(text = "Annuler", onClick = { model.editor = null }, kind = TeddButtonKind.Ghost)
            TeddButton(text = "Enregistrer", onClick = { model.saveFamily(existing, name, description, color, icon) }, kind = TeddButtonKind.Primary, enabled = name.isNotBlank())
        },
    ) {
        TeddField(label = "Nom", value = name, onValueChange = { name = it }, placeholder = "Données, Communication, Gestion de projet…")
        PfMultiline("Description", description, { description = it }, "Ce que cette famille regroupe.", minHeight = 72.dp)
        PaletteRow("Couleur", FAMILY_PALETTE, color, { color = it }, icon = false)
        PaletteRow("Icône", FAMILY_ICONS, icon, { icon = it }, icon = true)
    }
}

// ── Compétence ─────────────────────────────────────────────────────────────

@Composable
internal fun CompetenceEditorDialog(existing: Competence?, initialFamilyId: String?, model: PortfolioModel) {
    var familyId by remember(existing) { mutableStateOf(existing?.family_id ?: initialFamilyId ?: model.families.firstOrNull()?.id ?: "") }
    var name by remember(existing) { mutableStateOf(existing?.name ?: "") }
    var description by remember(existing) { mutableStateOf(existing?.description ?: "") }
    var skillId by remember(existing) { mutableStateOf(existing?.skill_id) }
    EditorChrome(
        title = if (existing == null) "Nouvelle compétence" else "Modifier la compétence",
        onClose = { model.editor = null },
        actions = {
            if (existing != null) {
                TeddButton(text = "Supprimer", onClick = { model.editor = null; model.confirmDelete = DeleteTarget.Competence(existing) }, kind = TeddButtonKind.Danger)
            }
            Spacer(modifier = Modifier.weight(1f))
            TeddButton(text = "Annuler", onClick = { model.editor = null }, kind = TeddButtonKind.Ghost)
            TeddButton(text = "Enregistrer", onClick = { model.saveCompetence(existing, familyId, name, description, skillId) }, kind = TeddButtonKind.Primary, enabled = name.isNotBlank() && familyId.isNotBlank())
        },
    ) {
        PfSelect(
            label = "Famille",
            options = model.families.map { it.id to it.name },
            selected = familyId,
            onSelect = { it?.let { v -> familyId = v } },
            placeholder = "Choisir une famille",
        )
        TeddField(label = "Nom", value = name, onValueChange = { name = it }, placeholder = "SQL, Animer une réunion, Modélisation…")
        PfMultiline("Description", description, { description = it }, "Ce que savoir faire veut dire ici.", minHeight = 72.dp)
        PfSelect(
            label = "Compétence suivie dans Focus (optionnel)",
            options = listOf<Pair<String?, String>>(null to "Aucune") + model.skills.filter { it.archived == 0L }.map { it.id to it.name },
            selected = skillId,
            onSelect = { skillId = it },
            placeholder = "Aucune",
        )
        Text(
            "Si tu la relies, les heures de focus et le palier s'affichent à côté. Le niveau du recueil reste ton auto-évaluation.",
            color = TeddTokens.InkMuted, fontSize = 12.sp, lineHeight = 17.sp,
        )
    }
}

// ── Critère ────────────────────────────────────────────────────────────────

@Composable
internal fun CriterionEditorDialog(existing: Competence_criterion?, competenceId: String, model: PortfolioModel) {
    var label by remember(existing) { mutableStateOf(existing?.label ?: "") }
    var description by remember(existing) { mutableStateOf(existing?.description ?: "") }
    var level by remember(existing) { mutableStateOf(CompetenceLevel.of(existing?.level)) }
    var note by remember(existing) { mutableStateOf(existing?.evaluation_note ?: "") }
    EditorChrome(
        title = if (existing == null) "Nouveau critère" else "Modifier le critère",
        onClose = { model.editor = null },
        actions = {
            if (existing != null) {
                TeddButton(text = "Supprimer", onClick = { model.editor = null; model.confirmDelete = DeleteTarget.Criterion(existing) }, kind = TeddButtonKind.Danger)
            }
            Spacer(modifier = Modifier.weight(1f))
            TeddButton(text = "Annuler", onClick = { model.editor = null }, kind = TeddButtonKind.Ghost)
            TeddButton(
                text = "Enregistrer",
                onClick = {
                    model.saveCriterion(existing, competenceId, label, description, level.value)
                    if (existing != null && note.trim() != existing.evaluation_note.orEmpty()) model.setLevelNote(existing, note)
                },
                kind = TeddButtonKind.Primary,
                enabled = label.isNotBlank(),
            )
        },
    ) {
        TeddField(label = "Critère", value = label, onValueChange = { label = it }, placeholder = "Écrire une jointure sur trois tables")
        PfMultiline("Description", description, { description = it }, "Comment on reconnaît que c'est acquis.", minHeight = 72.dp)
        Column {
            PfEyebrow("Niveau", modifier = Modifier.padding(bottom = 6.dp))
            LevelPicker(level, onChange = { level = it })
        }
        if (existing != null) {
            PfMultiline("Note d'évaluation", note, { note = it }, "Où et quand tu l'as observé.", minHeight = 60.dp)
        }
    }
}

// ── Pièce ──────────────────────────────────────────────────────────────────

@Composable
internal fun PieceEditorDialog(existing: Portfolio_piece?, model: PortfolioModel) {
    var title by remember(existing) { mutableStateOf(existing?.title ?: "") }
    var subtitle by remember(existing) { mutableStateOf(existing?.subtitle ?: "") }
    var body by remember(existing) { mutableStateOf(existing?.body ?: "") }
    var dateStart by remember(existing) { mutableStateOf(existing?.date_start ?: "") }
    var dateEnd by remember(existing) { mutableStateOf(existing?.date_end ?: "") }
    var projectId by remember(existing) { mutableStateOf(existing?.project_id) }
    var stepId by remember(existing) { mutableStateOf(existing?.step_id) }
    var status by remember(existing) { mutableStateOf(existing?.status ?: PieceStatus.DRAFT) }
    var links by remember(existing) { mutableStateOf(PortfolioCodecs.decodeLinks(existing?.links)) }
    var images by remember(existing) { mutableStateOf(PortfolioCodecs.decodeImages(existing?.images)) }
    // Une nouvelle pièce a son identifiant d'avance : ses images de l'appareil s'y rattachent.
    val pieceId = remember(existing) { existing?.id ?: org.teddapp.project.db.newId("pp") }
    val scope = androidx.compose.runtime.rememberCoroutineScope()
    var imageError by remember { mutableStateOf<String?>(null) }
    var refs by remember(existing) {
        mutableStateOf<List<PieceCompetenceRef>>(
            existing?.let { p -> model.linksOf(p.id).map { PieceCompetenceRef(it.competence_id, it.criterion_id, it.note) } } ?: emptyList(),
        )
    }
    var pickingCompetence by remember { mutableStateOf(false) }

    EditorChrome(
        title = if (existing == null) "Nouvelle pièce" else "Modifier la pièce",
        onClose = { model.editor = null },
        actions = {
            if (existing != null) {
                TeddButton(text = "Supprimer", onClick = { model.editor = null; model.confirmDelete = DeleteTarget.Piece(existing) }, kind = TeddButtonKind.Danger)
            }
            Spacer(modifier = Modifier.weight(1f))
            TeddButton(text = "Annuler", onClick = { model.editor = null }, kind = TeddButtonKind.Ghost)
            TeddButton(
                text = if (status == PieceStatus.PUBLISHED) "Enregistrer et publier" else "Enregistrer",
                onClick = { model.savePiece(existing, title, subtitle, body, links, images, dateStart, dateEnd, projectId, stepId, status, refs, newId = pieceId) },
                kind = TeddButtonKind.Primary,
                enabled = title.isNotBlank(),
            )
        },
    ) {
        TeddField(label = "Titre", value = title, onValueChange = { title = it }, placeholder = "Tableau de bord des ventes, Refonte du site…")
        TeddField(label = "Contexte", value = subtitle, onValueChange = { subtitle = it }, placeholder = "Pour qui, dans quel cadre, en une ligne.")
        Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            TeddDateField(label = "Début", value = dateStart, onValueChange = { dateStart = it }, placeholder = "Sans date", modifier = Modifier.weight(1f))
            TeddDateField(label = "Fin", value = dateEnd, onValueChange = { dateEnd = it }, placeholder = "Sans date", modifier = Modifier.weight(1f), minIso = dateStart.takeIf { it.isNotBlank() })
        }
        Column {
            PfEyebrow("Statut", modifier = Modifier.padding(bottom = 6.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                listOf(PieceStatus.DRAFT to "Brouillon", PieceStatus.PUBLISHED to "Publiée").forEach { (v, l) ->
                    val on = status == v
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RFull))
                            .background(if (on) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { status = v }
                            .padding(horizontal = 12.dp, vertical = 6.dp),
                    ) { Text(l, color = if (on) TeddTokens.OnAction else TeddTokens.Ink, fontSize = 12.sp, fontWeight = FontWeight.SemiBold) }
                }
            }
        }
        PfMultiline(
            "Récit (markdown)", body, { body = it },
            "Le problème, ce que tu as fait, ce que ça a produit. Titres avec #, listes avec -, **gras**.",
            minHeight = 160.dp, mono = true,
        )
        if (model.projects.isNotEmpty()) {
            PfSelect(
                label = "Projet rattaché (optionnel)",
                options = listOf<Pair<String?, String>>(null to "Aucun") + model.projects.map { it.id to it.name },
                selected = projectId,
                onSelect = { projectId = it },
            )
        }
        if (model.steps.isNotEmpty()) {
            PfSelect(
                label = "Étape de parcours (optionnel)",
                options = listOf<Pair<String?, String>>(null to "Aucune") + model.steps.map { it.id to "${it.code} · ${it.title}" },
                selected = stepId,
                onSelect = { stepId = it },
            )
        }

        // Liens
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                PfEyebrow("Liens", modifier = Modifier.weight(1f))
                TeddButton(text = "Lien", onClick = { links = links + PieceLink("", "") }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, leadingIcon = { TeddIcon(name = "plus", size = 11.dp, color = TeddTokens.InkMuted) })
            }
            links.forEachIndexed { i, l ->
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalAlignment = Alignment.Bottom) {
                    TeddField(label = "Libellé", value = l.label, onValueChange = { v -> links = links.mapIndexed { j, x -> if (j == i) x.copy(label = v) else x } }, placeholder = "Dépôt", modifier = Modifier.weight(0.8f))
                    TeddField(label = "URL", value = l.url, onValueChange = { v -> links = links.mapIndexed { j, x -> if (j == i) x.copy(url = v) else x } }, placeholder = "https://…", modifier = Modifier.weight(1.4f))
                    TeddIconButton(onClick = { links = links.filterIndexed { j, _ -> j != i } }, size = 40.dp) { TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted) }
                }
            }
        }

        // Images : depuis l'appareil (un fichier joint, envoyé avec la synchronisation) ou par adresse web.
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                PfEyebrow("Images", modifier = Modifier.weight(1f))
                if (org.teddapp.project.ui.admin.TeddImagePicker.pick != null) TeddButton(
                    text = "Depuis l'appareil",
                    onClick = {
                        scope.launch {
                            val pick = org.teddapp.project.ui.admin.TeddImagePicker.pick ?: return@launch
                            val picked = runCatching { pick() }.getOrNull() ?: return@launch
                            if (picked.bytes.size > org.teddapp.project.ui.admin.TeddImagePicker.MAX_BYTES) { imageError = "Cette image est trop lourde (6 Mo au plus)."; return@launch }
                            imageError = null
                            images = images + PieceImages.store(model.repos.db, pieceId, picked)
                        }
                    },
                    kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm,
                    leadingIcon = { TeddIcon(name = "plus", size = 11.dp, color = TeddTokens.InkMuted) },
                )
                TeddButton(text = "Par adresse", onClick = { images = images + PieceImage("", "") }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, leadingIcon = { TeddIcon(name = "plus", size = 11.dp, color = TeddTokens.InkMuted) })
            }
            imageError?.let { Text(it, color = TeddTokens.Danger, style = TeddType.Caption) }
            images.forEachIndexed { i, img ->
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalAlignment = Alignment.Bottom) {
                    if (PieceImages.isFile(img.url)) {
                        PieceImageView(db = model.repos.db, image = img, modifier = Modifier.size(56.dp).clip(RoundedCornerShape(TeddTokens.RSm)))
                    } else {
                        TeddField(label = "URL", value = img.url, onValueChange = { v -> images = images.mapIndexed { j, x -> if (j == i) x.copy(url = v) else x } }, placeholder = "https://…/capture.png", modifier = Modifier.weight(1.4f))
                    }
                    TeddField(label = "Légende", value = img.caption, onValueChange = { v -> images = images.mapIndexed { j, x -> if (j == i) x.copy(caption = v) else x } }, placeholder = "Capture", modifier = Modifier.weight(if (PieceImages.isFile(img.url)) 2.2f else 0.8f))
                    TeddIconButton(onClick = { images = images.filterIndexed { j, _ -> j != i } }, size = 40.dp) { TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted) }
                }
            }
        }

        // Compétences démontrées
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                PfEyebrow("Compétences démontrées · ${refs.size}", modifier = Modifier.weight(1f))
                if (model.competences.isNotEmpty()) {
                    TeddButton(text = "Rattacher", onClick = { pickingCompetence = !pickingCompetence }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, leadingIcon = { TeddIcon(name = "plus", size = 11.dp, color = TeddTokens.InkMuted) })
                }
            }
            if (model.competences.isEmpty()) {
                Text("Crée d'abord des compétences dans le recueil pour les rattacher ici.", color = TeddTokens.InkMuted, fontSize = 12.sp)
            }
            if (pickingCompetence) {
                Column(
                    modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(TeddTokens.RSm)).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)).padding(8.dp),
                    verticalArrangement = Arrangement.spacedBy(2.dp),
                ) {
                    model.families.forEach { fam ->
                        val members = model.competences.filter { it.family_id == fam.id && refs.none { r -> r.competenceId == it.id && r.criterionId == null } }
                        if (members.isEmpty()) return@forEach
                        Text(fam.name.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(start = 6.dp, top = 6.dp, bottom = 2.dp))
                        members.forEach { c ->
                            Text(
                                c.name, color = TeddTokens.Ink, fontSize = 13.sp,
                                modifier = Modifier.fillMaxWidth()
                                    .clip(RoundedCornerShape(TeddTokens.RXs))
                                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                                        refs = refs + PieceCompetenceRef(c.id, null, null); pickingCompetence = false
                                    }
                                    .padding(horizontal = 8.dp, vertical = 6.dp),
                            )
                        }
                    }
                }
            }
            refs.forEachIndexed { i, ref ->
                val competence = model.competencesById[ref.competenceId]
                val crits = model.criteriaOf(ref.competenceId)
                Column(
                    modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(TeddTokens.RSm)).border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm)).padding(10.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(competence?.name ?: "Compétence supprimée", color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                        TeddIconButton(onClick = { refs = refs.filterIndexed { j, _ -> j != i } }, size = 26.dp) { TeddIcon(name = "trash", size = 12.dp, color = TeddTokens.InkMuted) }
                    }
                    if (crits.isNotEmpty()) {
                        PfSelect(
                            label = "Critère précis (optionnel)",
                            options = listOf<Pair<String?, String>>(null to "Compétence entière") + crits.map { it.id to it.label },
                            selected = ref.criterionId,
                            onSelect = { v -> refs = refs.mapIndexed { j, r -> if (j == i) r.copy(criterionId = v) else r } },
                        )
                    }
                    TeddField(
                        label = "Comment la pièce le montre",
                        value = ref.note.orEmpty(),
                        onValueChange = { v -> refs = refs.mapIndexed { j, r -> if (j == i) r.copy(note = v) else r } },
                        placeholder = "Une phrase, facultative.",
                    )
                }
            }
        }
    }
}
